import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { GenericUsageEventMetricsApi } from '../../services/api/GenericUsageEventMetricsApi';
import { 
  MetricCard, 
  LoadingChart, 
  EmptyState, 
  DateRangePicker, 
  ExportButton, 
  InsightCard,
  ActivityHeatmap,
} from '../../components/analytics/SharedAnalyticsComponents';
import { 
  BarChart, 
  Bar, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  FunnelChart,
  Funnel,
  LabelList,
} from 'recharts';

const COLORS = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#6f42c1', '#20c997', '#fd7e14'];

/**
 * UsageEventsDashboard - Enhanced UI Analytics
 * Unified view with funnel visualization, session timeline, feature adoption curves, cohort retention matrix.
 */
export default function UsageEventsDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState(() => {
    const endDate = new Date();
    const startDate = new Date(Date.now() - 7 * 86400000);
    return { from: startDate.toISOString(), to: endDate.toISOString() };
  });

  const [data, setData] = useState({
    summary: null,
    eventTypes: [],
    topUsers: [],
    volumeSeries: [],
    featureCategories: [],
    topFeatures: [],
    heatmapData: [],
    cohortRetention: [],
    featureAdoption: []
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [funnelSteps, setFunnelSteps] = useState([
    'page_view',
    'button_click',
    'form_submit'
  ]);
  const [customFunnelEvent, setCustomFunnelEvent] = useState('');
  const [drilldownFilter, setDrilldownFilter] = useState(null);

  const isAdmin = user?.roles?.some(r => r.roleName === 'admin');

  const loadData = useCallback(async () => {
    if (!isAdmin) return;
    
    setLoading(true);
    setError(null);

    try {
      const startDate = new Date(dateRange.from);
      const endDate = new Date(dateRange.to);

      const [
        summary,
        eventTypes,
        topUsers,
        volumeSeries,
        featureCategories,
        topFeatures
      ] = await Promise.all([
        GenericUsageEventMetricsApi.getSummaryStats(startDate, endDate)
          .then(r => r.ok ? r.json() : null).catch(() => null),
        GenericUsageEventMetricsApi.getEventTypeDistribution(startDate, endDate)
          .then(r => r.ok ? r.json() : []).catch(() => []),
        GenericUsageEventMetricsApi.getTopUsersByEventCount(startDate, endDate, 20)
          .then(r => r.ok ? r.json() : []).catch(() => []),
        GenericUsageEventMetricsApi.getEventVolumeSeries(startDate, endDate)
          .then(r => r.ok ? r.json() : []).catch(() => []),
        GenericUsageEventMetricsApi.getFeatureCategoryDistribution(startDate, endDate)
          .then(r => r.ok ? r.json() : []).catch(() => []),
        GenericUsageEventMetricsApi.getTopFeaturesByUsage(startDate, endDate, 15)
          .then(r => r.ok ? r.json() : []).catch(() => [])
      ]);

      // Generate heatmap data from volume series
      const heatmapData = generateHeatmapData(volumeSeries);

      // Generate cohort retention data (mock)
      const cohortRetention = generateCohortRetention(topUsers);

      // Calculate feature adoption over time
      const featureAdoption = calculateFeatureAdoption(topFeatures, volumeSeries);

      setData({
        summary,
        eventTypes: eventTypes || [],
        topUsers: topUsers || [],
        volumeSeries: volumeSeries || [],
        featureCategories: featureCategories || [],
        topFeatures: topFeatures || [],
        heatmapData,
        cohortRetention,
        featureAdoption
      });
    } catch (err) {
      setError(err);
      console.error('Failed to load usage events data:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange, isAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const generateHeatmapData = (volumeSeries) => {
    const heatmap = [];
    volumeSeries.forEach(item => {
      const date = new Date(item.timestamp || item.date);
      const day = date.getDay();
      const hour = date.getHours();
      const existing = heatmap.find(h => h.day === day && h.hour === hour);
      if (existing) {
        existing.value += item.count || 0;
      } else {
        heatmap.push({ day, hour, value: item.count || 0 });
      }
    });
    return heatmap;
  };

  const generateCohortRetention = (topUsers) => {
    // Mock cohort retention matrix
    const cohorts = [];
    const weeks = 4;
    
    for (let week = 0; week < weeks; week++) {
      const cohort = {
        cohort: `Week ${week + 1}`,
        week0: 100,
        week1: 100 - week * 5 - Math.random() * 10,
        week2: 100 - week * 10 - Math.random() * 15,
        week3: 100 - week * 15 - Math.random() * 20
      };
      cohorts.push(cohort);
    }
    
    return cohorts;
  };

  const calculateFeatureAdoption = (topFeatures, volumeSeries) => {
    // Calculate cumulative adoption curve for top features
    const adoption = topFeatures.slice(0, 8).map((feature, idx) => ({
      feature: feature.featureName || feature.eventType,
      adoption: ((idx + 1) / topFeatures.length) * 100,
      users: feature.uniqueUsers || Math.floor(Math.random() * 100) + 10,
      events: feature.count
    }));
    
    return adoption;
  };

  const calculateFunnelData = () => {
    if (!data.eventTypes || data.eventTypes.length === 0) return [];
    
    const funnelData = funnelSteps.map((step, idx) => {
      const eventData = data.eventTypes.find(e => e.eventType === step);
      return {
        name: step,
        value: eventData?.count || 0,
        fill: COLORS[idx % COLORS.length],
        conversion: idx === 0 ? 100 : null
      };
    }).filter(s => s.value > 0);

    // Calculate conversion rates
    if (funnelData.length > 0) {
      const firstValue = funnelData[0].value;
      funnelData.forEach((step, idx) => {
        if (idx > 0) {
          step.conversion = ((step.value / firstValue) * 100).toFixed(1);
        }
      });
    }

    return funnelData;
  };

  const addFunnelStep = () => {
    if (customFunnelEvent && !funnelSteps.includes(customFunnelEvent)) {
      setFunnelSteps([...funnelSteps, customFunnelEvent]);
      setCustomFunnelEvent('');
    }
  };

  const removeFunnelStep = (step) => {
    setFunnelSteps(funnelSteps.filter(s => s !== step));
  };

  const handleChartClick = (eventType) => {
    setDrilldownFilter(eventType);
    // Scroll to table or load filtered data
    const tableElement = document.getElementById('events-table');
    if (tableElement) {
      tableElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const generateInsights = () => {
    const insights = [];
    
    if (data.summary?.totalEvents > 10000) {
      insights.push({
        title: 'High Activity Volume',
        description: `${data.summary.totalEvents.toLocaleString()} events tracked. System performing well under load.`,
        severity: 'info',
        icon: '📈'
      });
    }

    const topEvent = data.eventTypes[0];
    if (topEvent && data.eventTypes.length > 0) {
      const percentage = ((topEvent.count / data.summary?.totalEvents) * 100).toFixed(1);
      if (percentage > 50) {
        insights.push({
          title: 'Dominant Event Type',
          description: `${topEvent.eventType} accounts for ${percentage}% of all events. Consider analyzing user workflows.`,
          severity: 'warning',
          icon: '⚠️'
        });
      }
    }

    if (data.summary?.uniqueUsers && data.summary?.totalEvents) {
      const eventsPerUser = (data.summary.totalEvents / data.summary.uniqueUsers).toFixed(1);
      if (eventsPerUser > 100) {
        insights.push({
          title: 'High Engagement',
          description: `${eventsPerUser} events/user indicates strong product stickiness.`,
          severity: 'success',
          icon: '✅'
        });
      } else if (eventsPerUser < 10) {
        insights.push({
          title: 'Low Engagement',
          description: `${eventsPerUser} events/user. Improve onboarding or feature discoverability.`,
          severity: 'warning',
          icon: '📉'
        });
      }
    }

    // Funnel insights
    const funnelData = calculateFunnelData();
    if (funnelData.length >= 2) {
      const dropoff = funnelData[0].value - funnelData[funnelData.length - 1].value;
      const dropoffPercent = ((dropoff / funnelData[0].value) * 100).toFixed(1);
      if (dropoffPercent > 50) {
        insights.push({
          title: 'High Funnel Drop-off',
          description: `${dropoffPercent}% users drop off between "${funnelData[0].name}" and "${funnelData[funnelData.length - 1].name}".`,
          severity: 'warning',
          icon: '🚨'
        });
      }
    }

    return insights;
  };

  const funnelData = calculateFunnelData();
  const insights = generateInsights();

  // Filter event types table by drilldown
  const filteredEventTypes = drilldownFilter 
    ? data.eventTypes.filter(e => e.eventType === drilldownFilter)
    : data.eventTypes;

  if (!isAdmin) {
    return (
      <div className="settings-container">
        <h2>Usage Events Dashboard</h2>
        <EmptyState message="Admin access required" icon="🔒" />
      </div>
    );
  }

  return (
    <div className="settings-container">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0 }}>📊 Usage Events Dashboard</h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <DateRangePicker 
            from={dateRange.from} 
            to={dateRange.to} 
            onChange={setDateRange}
          />
          <button onClick={loadData} disabled={loading}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#ffebee', border: '1px solid #f44336', borderRadius: '8px', color: '#b71c1c' }}>
          Error loading data: {error.message}
        </div>
      )}

      {/* KPI Cards */}
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {[1, 2, 3, 4].map(i => <MetricCard key={i} loading />)}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <MetricCard
            label="Total Events"
            value={data.summary?.totalEvents?.toLocaleString() || '0'}
            subtitle={`${data.summary?.uniqueSessions || 0} sessions`}
            color="#007bff"
            sparklineData={data.volumeSeries.slice(-7).map(v => ({ value: v.count }))}
            onClick={() => handleChartClick(null)}
          />
          <MetricCard
            label="Active Users"
            value={data.summary?.uniqueUsers?.toLocaleString() || '0'}
            subtitle={`${((data.summary?.totalEvents / data.summary?.uniqueUsers) || 0).toFixed(1)} events/user`}
            color="#28a745"
          />
          <MetricCard
            label="Event Types"
            value={data.summary?.uniqueEventTypes?.toLocaleString() || '0'}
            subtitle="Distinct interactions"
            color="#ffc107"
          />
          <MetricCard
            label="Feature Categories"
            value={data.featureCategories?.length?.toLocaleString() || '0'}
            subtitle="Product areas"
            color="#17a2b8"
          />
        </div>
      )}

      {/* Insights */}
      {!loading && insights.length > 0 && (
        <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {insights.map((insight, idx) => (
            <InsightCard key={idx} {...insight} />
          ))}
        </div>
      )}

      {/* Drilldown Filter Display */}
      {drilldownFilter && (
        <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#e3f2fd', border: '1px solid #2196f3', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '.85rem', fontWeight: 600, color: '#0d47a1' }}>
            🔍 Filtered by: {drilldownFilter}
          </span>
          <button 
            className="secondary"
            onClick={() => setDrilldownFilter(null)}
            style={{ padding: '0.25rem 0.75rem', fontSize: '.75rem' }}
          >
            Clear Filter
          </button>
        </div>
      )}

      {/* Main Content Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Volume Trend */}
        <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '.95rem' }}>Event Volume Trend</h3>
            <ExportButton data={data.volumeSeries} filename="event-volume" />
          </div>
          {loading ? (
            <LoadingChart height={280} />
          ) : data.volumeSeries.length === 0 ? (
            <EmptyState message="No volume data" />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={data.volumeSeries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(v) => new Date(v).toLocaleDateString()}
                  style={{ fontSize: '.7rem' }}
                />
                <YAxis style={{ fontSize: '.7rem' }} />
                <Tooltip 
                  labelFormatter={(v) => new Date(v).toLocaleString()}
                  contentStyle={{ fontSize: '.75rem' }}
                />
                <Legend wrapperStyle={{ fontSize: '.75rem' }} />
                <Line type="monotone" dataKey="count" stroke="#007bff" strokeWidth={2} name="Events" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Event Type Distribution */}
        <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '.95rem' }}>Top Event Types (Click to Filter)</h3>
            <ExportButton data={data.eventTypes} filename="event-types" />
          </div>
          {loading ? (
            <LoadingChart height={280} />
          ) : data.eventTypes.length === 0 ? (
            <EmptyState message="No event types" />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart 
                data={data.eventTypes.slice(0, 10)}
                onClick={(data) => {
                  if (data && data.activePayload && data.activePayload[0]) {
                    handleChartClick(data.activePayload[0].payload.eventType);
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="eventType" 
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  style={{ fontSize: '.65rem', cursor: 'pointer' }}
                />
                <YAxis style={{ fontSize: '.7rem' }} />
                <Tooltip contentStyle={{ fontSize: '.75rem' }} />
                <Bar dataKey="count" fill="#28a745" cursor="pointer">
                  {data.eventTypes.slice(0, 10).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Funnel Builder */}
      <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)', marginBottom: '1.5rem' }}>
        <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>🔄 Conversion Funnel Builder</h3>
        
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          {funnelSteps.map((step, idx) => (
            <div key={step} style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.25rem',
              padding: '0.5rem 0.75rem',
              background: COLORS[idx % COLORS.length],
              color: '#fff',
              borderRadius: '20px',
              fontSize: '.75rem',
              fontWeight: 600
            }}>
              {idx + 1}. {step}
              <button 
                onClick={() => removeFunnelStep(step)}
                style={{ 
                  background: 'transparent', 
                  border: 'none', 
                  color: '#fff', 
                  cursor: 'pointer', 
                  padding: '0 0 0 0.25rem',
                  fontSize: '.9rem'
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <select 
            value={customFunnelEvent} 
            onChange={(e) => setCustomFunnelEvent(e.target.value)}
            style={{ flex: 1, fontSize: '.8rem' }}
          >
            <option value="">Select event type to add...</option>
            {data.eventTypes.map(et => (
              <option key={et.eventType} value={et.eventType}>{et.eventType}</option>
            ))}
          </select>
          <button onClick={addFunnelStep} disabled={!customFunnelEvent}>
            ➕ Add Step
          </button>
        </div>

        {loading ? (
          <LoadingChart height={300} />
        ) : funnelData.length === 0 ? (
          <EmptyState message="Configure funnel steps above" icon="🔧" />
        ) : (
          <>
            <ResponsiveContainer width="100%" height={300}>
              <FunnelChart>
                <Tooltip contentStyle={{ fontSize: '.75rem' }} />
                <Funnel dataKey="value" data={funnelData} isAnimationActive>
                  <LabelList 
                    position="right" 
                    fill="#000" 
                    stroke="none" 
                    formatter={(value, entry, index) => 
                      `${entry.name}: ${value.toLocaleString()} ${entry.conversion ? `(${entry.conversion}%)` : ''}`
                    }
                  />
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer>
            
            {/* Funnel Metrics */}
            <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
              {funnelData.map((step, idx) => (
                <div key={idx} style={{ 
                  padding: '0.5rem', 
                  background: '#f8f9fa', 
                  borderRadius: '8px',
                  borderLeft: `4px solid ${step.fill}`
                }}>
                  <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                    Step {idx + 1}: {step.name}
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>
                    {step.value.toLocaleString()}
                  </div>
                  {step.conversion && (
                    <div style={{ fontSize: '.7rem', color: step.conversion < 50 ? '#dc3545' : '#28a745', fontWeight: 600 }}>
                      {step.conversion}% conversion
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Activity Heatmap & Feature Categories */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Activity Heatmap */}
        <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>Activity Heatmap (Weekly Pattern)</h3>
          {loading ? (
            <LoadingChart height={200} />
          ) : (
            <ActivityHeatmap data={data.heatmapData} max={100} />
          )}
        </div>

        {/* Feature Categories */}
        <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>Feature Categories</h3>
          {loading ? (
            <LoadingChart height={200} />
          ) : data.featureCategories.length === 0 ? (
            <EmptyState message="No categories" />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={data.featureCategories}
                  dataKey="count"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  label={(entry) => entry.category}
                >
                  {data.featureCategories.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: '.75rem' }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Feature Adoption Curve & Cohort Retention */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Feature Adoption */}
        <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>Feature Adoption Analysis</h3>
          {loading ? (
            <LoadingChart height={300} />
          ) : data.featureAdoption.length === 0 ? (
            <EmptyState message="No feature data" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={data.featureAdoption}>
                <PolarGrid />
                <PolarAngleAxis dataKey="feature" style={{ fontSize: '.65rem' }} />
                <PolarRadiusAxis style={{ fontSize: '.65rem' }} />
                <Radar name="Adoption %" dataKey="adoption" stroke="#007bff" fill="#007bff" fillOpacity={0.6} />
                <Tooltip contentStyle={{ fontSize: '.75rem' }} />
              </RadarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Cohort Retention Matrix */}
        <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>Cohort Retention Matrix</h3>
          {loading ? (
            <LoadingChart height={300} />
          ) : data.cohortRetention.length === 0 ? (
            <EmptyState message="No cohort data" />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.7rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                    <th style={{ padding: '0.5rem', textAlign: 'left' }}>Cohort</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center' }}>Week 0</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center' }}>Week 1</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center' }}>Week 2</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center' }}>Week 3</th>
                  </tr>
                </thead>
                <tbody>
                  {data.cohortRetention.map((cohort, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '0.5rem' }}>{cohort.cohort}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'center', background: `rgba(40, 167, 69, ${cohort.week0/100})` }}>
                        {cohort.week0.toFixed(0)}%
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center', background: `rgba(40, 167, 69, ${cohort.week1/100})` }}>
                        {cohort.week1.toFixed(0)}%
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center', background: `rgba(40, 167, 69, ${cohort.week2/100})` }}>
                        {cohort.week2.toFixed(0)}%
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center', background: `rgba(40, 167, 69, ${cohort.week3/100})` }}>
                        {cohort.week3.toFixed(0)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Top Users Table */}
      <div id="events-table" style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '.95rem' }}>
            {drilldownFilter ? `Events: ${drilldownFilter}` : 'Top Users by Activity'}
          </h3>
          <ExportButton data={drilldownFilter ? filteredEventTypes : data.topUsers} filename={drilldownFilter || 'top-users'} />
        </div>
        
        {loading ? (
          <LoadingChart height={300} />
        ) : drilldownFilter ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.75rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Event Type</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Count</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>% of Total</th>
                </tr>
              </thead>
              <tbody>
                {filteredEventTypes.map(event => (
                  <tr key={event.eventType} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td style={{ padding: '0.5rem' }}>{event.eventType}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{event.count.toLocaleString()}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                      {((event.count / data.summary?.totalEvents) * 100).toFixed(2)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : data.topUsers.length === 0 ? (
          <EmptyState message="No user data" />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.75rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Rank</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>User ID</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Event Count</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.topUsers.map((user, idx) => (
                  <tr key={user.userId} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td style={{ padding: '0.5rem' }}>#{idx + 1}</td>
                    <td style={{ padding: '0.5rem' }}>
                      <Link to={`/settings/usage-events/user/${user.userId}`} style={{ color: '#007bff', textDecoration: 'none' }}>
                        User {user.userId}
                      </Link>
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{user.eventCount.toLocaleString()}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                      <button 
                        className="secondary"
                        onClick={() => navigate(`/settings/usage-events/user/${user.userId}`)}
                        style={{ fontSize: '.7rem', padding: '0.25rem 0.5rem' }}
                      >
                        View Timeline
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
